import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import {
  API_CAR_TYPES,
  API_CONFIGURABLES,
  API_DISTRICTS,
  API_SERVICE_TYPES,
  API_VEHICLE_TYPES,
} from "../../utils/apis";
import {
  createColumn,
  createEditAction,
  createViewAction,
  getRegister,
  numberFormat,
} from "../../utils/helpers";
import { getForm } from "../../utils/helpers";
import { URL_SURCHARGE_CONFIGURABLES } from "../../utils/urls";
import useGet from "../common/useGet";
import { IFormSection } from "../../types/common";

export const useSurchargeConfigurables = () => {
  const tableColumns = () => [
    createColumn("service_name", true, "Service Type"),
    createColumn("car_type", true, "Car Type"),
    createColumn("car_size", true, "Car Size"),
    createColumn("vehicle_type", true, "Vehicle Type"),
    // createColumn("vehicle_type_dependant", true, "Vehicle Type Dependant"),
    // createColumn("car_type_dependant", true, "Car Type Dependant"),
    // createColumn("car_size_dependant", true, "Car Size Dependant"),
    createColumn("attach_original_rate", true, "Attach Original Rate"),
    createColumn(
      "attach_previous_surcharge",
      true,
      "Attach Previous Surcharge",
    ),
    createColumn("duration", true, "Duration"),
    createColumn("rate_type", false, "Rate Type"),
    createColumn("min_range", false, "Min Range"),
    createColumn("max_range", false, "Max Range"),
    createColumn("amount", false, "Amount"),
    createColumn("city_name", true, "City"),
    createColumn("auto_run", true, "Auto Run"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        amount: numberFormat(item?.amount ? item.amount : 0),
        // car_size_dependant: item?.car_size_dependant ? "Yes" : "No",
        // car_type_dependant: item?.car_type_dependant ? "Yes" : "No",
        // vehicle_type_dependant: item?.vehicle_type_dependant ? "Yes" : "No",
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_SURCHARGE_CONFIGURABLES, item?.id),
              createEditAction(URL_SURCHARGE_CONFIGURABLES, item?.id),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Surcharge Configurable Details",
          fields: {
            service_type: item?.service_type_name,
            car_size: item?.car_size_name,
            car_type: item?.car_type_name,
            vehicle_type: item?.vehicle_type_name,
            vehicle_type_dependant: item?.vehicle_type_dependant ? "Yes" : "No",
            car_type_dependant: item?.car_type_dependant ? "Yes" : "No",
            car_size_dependant: item?.car_size_dependant ? "Yes" : "No",
            attach_original_rate: item?.attach_original_rate ? "Yes" : "No",
            attach_previous_surcharge: item?.attach_previous_surcharge
              ? "Yes"
              : "No",
            duration: item?.duration,
            rate_type: item?.rate_type,
            min_range: item?.min_range,
            max_range: item?.max_range,
            amount: item?.amount,
            city: item?.city_name,
            auto_run: item?.auto_run ? "Yes" : "No",
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_SURCHARGE_CONFIGURABLES);

  return { tableColumns, tableData, viewData, formRoute };
};

export const useSurchargeConfigurablesForm = ({ updateFormValues }) => {
  const {
    loadData: loadServiceTypes,
    loading: loadingServiceTypes,
    data: serviceTypes,
  } = useGet();

  const {
    loadData: loadVehicleTypes,
    loading: loadingVehicleTypes,
    data: vehicleTypes,
  } = useGet();

  const {
    loadData: loadCarTypes,
    loading: loadingCarTypes,
    data: carTypes,
  } = useGet();

  const {
    loadData: loadCarSizes,
    loading: loadingCarSizes,
    data: carSizes,
  } = useGet();

  const {
    loadData: loadCities,
    loading: loadingCities,
    data: cities,
  } = useGet();

  useEffect(() => {
    // loadServiceRates({ api: API_SERVICE_RATES });
    loadCities({ api: API_DISTRICTS });
    loadServiceTypes({ api: API_SERVICE_TYPES });
    loadVehicleTypes({ api: API_VEHICLE_TYPES });
    loadCarTypes({ api: API_CAR_TYPES });
    loadCarSizes({ api: API_CONFIGURABLES, params: { key: "car_size" } });
  }, []);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues();
    }
  }, [updateFormValues]);

  const sections: IFormSection[] = [
    {
      title: "Surcharge Configurable Details",
      fields: [
        {
          field_name: "service_type",
          label: "Service Type",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Service Type",
            options:
              serviceTypes?.results?.map((item) => ({
                value: item.id,
                label: item.name,
              })) || [],
          },
        },
        {
          field_name: "city",
          label: "City",
          type: "select",
          props: {
            required: true,
            placeholder: "Select City",
            options:
              (cities &&
                cities?.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))) ||
              [],
          },
        },
        {
          field_name: "amount",
          label: "Amount",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter Amount",
          },
        },
        {
          field_name: "min_range",
          label: "Min Range",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter Min Range",
            min: 0,
          },
        },
        {
          field_name: "max_range",
          label: "Max Range",
          type: "number",
          props: { required: true, placeholder: "Enter Max Range" },
        },
        {
          field_name: "duration",
          label: "Duration",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Duration",
            options: [
              { value: "MINUTES", label: "Minutes" },
              { value: "HOURS", label: "Hours" },
              { value: "DAYS", label: "Days" },
              { value: "WEEKS", label: "Weeks" },
              { value: "MONTHS", label: "Months" },
            ],
          },
        },
        {
          field_name: "rate_type",
          label: "Rate Type",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Rate Type",
            options: [
              { value: "ONCE", label: "Once" },
              { value: "MINUTELY", label: "Minutely" },
              { value: "HOURLY", label: "Hourly" },
              { value: "DAILY", label: "Daily" },
              { value: "WEEKLY", label: "Weekly" },
              { value: "MONTHLY", label: "Monthly" },
            ],
          },
        },
        {
          field_name: "attach_previous_surcharge",
          label: "Attach Previous Surcharge",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Attach Previous Surcharge",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "attach_original_rate",
          label: "Attach Original Rate",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Attach Original Rate",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "auto_run",
          label: "Auto Run",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Auto Run",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "active",
          label:
            "Active(indicates if surcharge application is to be managed by job/service or its manual)",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Active",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "allow_partial_fullfillment",
          label: "Allow Partial Fullfillment",
          type: "select",
          props: {
            placeholder: "Select Allow Partial Fullfillment",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "minimum_partial_fullfillment",
          label: "Minimum Partial Fullfillment",
          type: "number",
          props: {
            placeholder: "Enter Minimum Partial Fullfillment",
            min: 0,
          },
        },
      ],
    },

    {
      title: "Car Details",
      fields: [
        {
          field_name: "car_size_dependant",
          label: "Car Size Dependant",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Car Size Dependant",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "car_type_dependant",
          label: "Car Type Dependant",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Car Type Dependant",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "vehicle_type_dependant",
          label: "Vehicle Type Dependant",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Vehicle Type Dependant",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "car_size",
          label: "Car Size",
          type: "select",
          props: {
            placeholder: "Select Car Size",
            options:
              carSizes?.results?.map((item) => ({
                value: item.id,
                label: item.name,
              })) || [],
          },
        },
        {
          field_name: "car_type",
          label: "Car Type",
          type: "select",
          props: {
            placeholder: "Select Car Type",
            options:
              carTypes?.results?.map((item) => ({
                value: item.id,
                label: item.name,
              })) || [],
          },
        },
        {
          field_name: "vehicle_type",
          label: "Vehicle Type",
          type: "select",
          props: {
            placeholder: "Select Vehicle Type",
            options:
              (vehicleTypes &&
                vehicleTypes?.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))) ||
              [],
          },
        },
      ],
    },
  ];

  const title = "Surcharge Configurable";
  const redirect = getRegister(URL_SURCHARGE_CONFIGURABLES);

  return {
    sections,
    title,
    redirect,
    serviceTypes,
    loadingServiceTypes,
    cities,
    loadingCities,
    vehicleTypes,
    loadingVehicleTypes,
    carTypes,
    loadingCarTypes,
    carSizes,
    loadingCarSizes,
  };
};
